// Footprint store — owns its own footprint fetch (no service class), driving the Shadow
// Identity "digital footprint" experience. Mirrors the setup-store + usePbApi
// pattern of stores/person/dark-web-scan.ts.
//
// The footprint is computed LIVE and never kept: the backend parks a running pass in a
// short-lived cache purely so an async job can hand its result back to the browser that
// asked for it, and NOTHING is restored across visits — arriving reads entitlement and scan
// status only, never a body. Durable changes are saved through the identity vault and
// family forms, with consent. There are no verdicts — waves show what a
// scan finds, the person picks out what is theirs, and everything else simply
// evaporates. Wave state (which values were seen and not added, and what the
// person decided about each one) lives HERE, for this session only.
//
// Every action calls the real /footprint endpoints (plugins/api.ts →
// routes.people.footprint) through usePbApi, apart from `loadMonitoring`, which asks the
// person-options service whether dark web monitoring is on: that answer has its own front
// door, and deriving it here would be a second rule. One transport, one path: an offline
// demonstration is served by services/demo, which answers those same routes, so the
// store cannot have a second behavior nothing ships.
import type {
    DatasetMeta,
    DeeperScanResult,
    DrawerRecordsError,
    FamilyAttribution,
    Footprint,
    FootprintAccessInfo,
    FootprintField,
    FootprintGroup,
    FootprintMeta,
    FootprintRecordsResponse,
    FootprintShowResponse,
    FootprintSession,
    FootprintSessionResponse,
    FootprintValue,
    FragmentDecision,
    FragmentDisposition,
    FragmentState,
    LeakedRecord,
    LeakedRecordField,
    RawLeakedRecord,
    RawSnapshot,
    RawSnapshotSensitive,
    RawSnapshotSource,
    RawSnapshotValue,
    ScanDeferralReason,
    ScanDepth,
    ScanRequestResponse,
    ScanStatus,
    Seed,
    SeedField,
    SensitiveRevealError,
    SensitiveRevealResponse,
    SensitiveRevealResult,
    UnattributedDisposition,
} from '~/types/person/footprint';
import { formatDateTime } from '@privacy-bee/format/date';
import { personOptionsService } from '~/services/person-options-service';
import { fieldKind } from '~/utils/footprint-fields';
import { JOURNEY_LEVELS } from '~/utils/footprint-levels';
import { plural } from '~/utils/plural';
import { EVIDENCE_COPY } from '~/utils/footprint-evidence-copy';

const DEPTH_ORDER: ScanDepth[] = ['quick', 'standard', 'deep'];

/** Safety cap on the request-and-poll read loop so a stuck scan can't spin forever. */
const MAX_POLLS = 60;

/** The themed groupings the flat value rows are sorted into for the view model. */
const GROUP_DEFS: Array<{ id: string; name: string; fields: string[] }> = [
    { id: 'identity', name: 'Identity', fields: ['first_name', 'last_name'] },
    { id: 'contact', name: 'Contact & online', fields: ['email', 'phone', 'username'] },
    { id: 'location', name: 'Location', fields: ['street_address', 'city', 'state', 'zip'] },
];

/** Contact names support attribution; email and phone details can also be saved to the member. */
export const FAMILY_FIELDS: ReadonlySet<string> = new Set(['first_name', 'last_name', 'email', 'phone']);

/**
 * Last-resort mask for a credential row whose server mask is empty. The backend owns the
 * masking, so this is a floor rather than a second masking implementation: a blank cell
 * would otherwise read as "no value found" over a value that was very much found.
 */
const SENSITIVE_PLACEHOLDER: Record<string, string> = { password: '••••••••', ssn: '•••-••-••••' };

const isSensitiveType = (fieldType: string) => fieldType === 'password' || fieldType === 'ssn';

/**
 * One decision row, built in ONE place. A live value and a record field reach this from
 * different vocabularies, and two builders would put two shapes of the same fragment in one
 * list. Credential ownership uses a sanitized entry with no credential value instead.
 */
const decisionOf = (
    id: string,
    field_type: string,
    display: string,
    normalized_value: string,
    disposition: FragmentDisposition,
    family: FamilyAttribution | null = null
): FragmentDecision => ({ id, field_type, display, normalized_value, disposition, family, sensitive: false });

/**
 * One reading order for every list of fragments: the kind of thing first, then the value. A
 * reader scanning for one address should not have to find it between two email addresses.
 */
const byKindThenValue = (a: { field_type: string; display: string }, b: { field_type: string; display: string }) =>
    fieldKind(a.field_type).label.localeCompare(fieldKind(b.field_type).label) || a.display.localeCompare(b.display);

const slug = (s: string) =>
    (s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

const depthFromInt = (n: number): ScanDepth => (!n || n <= 1 ? 'quick' : n === 2 ? 'standard' : 'deep');

const emptyMeta = (): FootprintMeta => ({
    person_name: null,
    source_count: 0,
    record_count: 0,
    scanned_at: null,
    duration_ms: null,
    depth: 'quick',
    pass: 1,
});

const sleep = (ms: number) => (ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve());

export const useFootprintStore = defineStore('footprint', () => {
    // ---- State -------------------------------------------------------------
    const footprint = ref<Footprint>({ groups: [] });
    const seeds = ref<Seed[]>([]);
    const depth = ref<ScanDepth>('quick');
    const status = ref<ScanStatus>('idle');
    const deferralReason = ref<ScanDeferralReason | null>(null);
    const retryAt = ref<number | null>(null);
    const deferralNotice = computed(() => {
        if (deferralReason.value === 'source_unavailable') return EVIDENCE_COPY.deferral.source_unavailable;
        const reason = deferralReason.value && EVIDENCE_COPY.deferral[deferralReason.value];
        const deadline = formatDateTime(retryAt.value);

        return `${reason || 'This scan is temporarily paused.'} ${deadline ? `Try again after ${deadline}.` : 'Try again later.'}`;
    });

    function applyDeferral(scan: FootprintShowResponse['status'], reason: ScanDeferralReason | null, retryAfter: number | null) {
        deferralReason.value = scan === 'deferred' ? reason : null;
        const deadline = typeof retryAfter === 'number' ? Date.now() + retryAfter * 1000 : NaN;
        retryAt.value = scan === 'deferred' && reason !== 'source_unavailable' && retryAfter !== null && retryAfter >= 0 && Number.isFinite(deadline)
            ? deadline
            : null;
    }
    const scanSession = ref<FootprintSession | null>(null);
    const sessionDeadline = ref<number | null>(null);
    const sessionClock = ref(Date.now());
    const sessionExtending = ref(false);
    const sessionError = ref('');
    const sessionNotice = ref('');
    const sessionEndCallbacks = new Set<() => void>();
    const sessionSecondsRemaining = computed(() => sessionDeadline.value === null
        ? null
        : Math.max(0, Math.ceil((sessionDeadline.value - sessionClock.value) / 1000)));
    const sessionWarning = computed(() => sessionSecondsRemaining.value !== null
        && sessionSecondsRemaining.value > 0 && sessionSecondsRemaining.value <= 59);

    /** Scanner-owned surfaces register their own cleanup; unrelated dialogs are never closed. */
    function onSessionEnd(close: () => void): () => void {
        sessionEndCallbacks.add(close);
        return () => { sessionEndCallbacks.delete(close); };
    }

    function endSession(reason: 'expired' | 'ended' | 'left' = 'ended') {
        cancelPolling();
        clearResultState();
        awaitingStart.value = true;
        status.value = 'idle';
        sessionNotice.value = reason === 'left' ? '' : reason === 'expired'
            ? 'Your dark web scan session expired. For your security, its temporary results were cleared. Start a new scan whenever you’re ready.'
            : 'Your dark web scan session has ended. Its results were cleared from this page.';
        announce(sessionNotice.value);
    }

    function checkSession(now = Date.now()) {
        sessionClock.value = now;
        if (sessionDeadline.value !== null && now >= sessionDeadline.value) endSession('expired');
    }

    /** Server time avoids clock skew; charging the round trip to the lease never extends it in transit. */
    function applySession(lease: FootprintSession | null, requestStartedAt: number): boolean {
        if (!lease) {
            if (scanSession.value) {
                endSession('expired');
                return false;
            }
            return true;
        }
        const expiresAt = Date.parse(lease.expires_at);
        const serverNow = Date.parse(lease.server_now);
        if (!lease.snapshot_id || !Number.isFinite(expiresAt) || !Number.isFinite(serverNow)) {
            endSession('ended');
            return false;
        }
        // A read begun before a successful extension cannot shorten that same snapshot's lease.
        if (scanSession.value?.snapshot_id === lease.snapshot_id
            && expiresAt < Date.parse(scanSession.value.expires_at)) return true;
        scanSession.value = lease;
        sessionDeadline.value = requestStartedAt + expiresAt - serverNow;
        sessionError.value = '';
        checkSession();
        return scanSession.value !== null;
    }

    async function extendSession(): Promise<boolean> {
        checkSession();
        const lease = scanSession.value;
        if (!lease || sessionExtending.value) return false;
        const isCurrentSession = captureSession();
        const stillCurrent = () => isCurrentSession() && scanSession.value?.snapshot_id === lease.snapshot_id;
        const requestStartedAt = Date.now();
        sessionExtending.value = true;
        sessionError.value = '';
        try {
            const nuxtApp = useNuxtApp();
            const response = await usePbApi<FootprintSessionResponse>(nuxtApp.$api.routes.people.footprint.show(personId), {
                method: 'PATCH',
                body: { snapshot_id: lease.snapshot_id, expected_expires_at: lease.expires_at },
            });
            if (!stillCurrent()) return false;
            checkSession();
            if (!stillCurrent()) return false;
            if (response.status === 409) {
                endSession(response.data?.error === 'scan_expired' ? 'expired' : 'ended');
                return false;
            }
            if (response.status === 200 && response.data?.session?.snapshot_id === lease.snapshot_id) {
                return applySession(response.data.session, requestStartedAt);
            }
            sessionError.value = 'We couldn’t extend this session. Try again before the countdown ends.';
            return false;
        } catch {
            if (stillCurrent()) sessionError.value = 'We couldn’t extend this session. Try again before the countdown ends.';
            return false;
        } finally {
            if (isCurrentSession()) sessionExtending.value = false;
        }
    }

    const truncated = ref(false);
    /**
     * The backend refused to search: the pass it ran had no identifier precise enough to build
     * a query from. Its verdict, not the client's, and the two disagree — a bare name, a street
     * with no locality and an unverified phone all look searchable here and compile to nothing
     * there — so an empty result under this flag means nothing was searched, never that nothing
     * was found.
     */
    const noUsableSeeds = ref(false);
    const meta = ref<FootprintMeta>(emptyMeta());
    /**
     * The sources that contributed to this snapshot, each with what the dataset catalogue knows
     * about it. Held as the backend sent them: the catalogue's keys are all nullable and read at
     * serialization time, so anything derived here would be a second, staler description of a
     * dataset the drill already describes.
     *
     * This list is BOUNDED (`sources_truncated`) while `meta.source_count` is the whole-snapshot
     * total, so the two answer different questions and `.length` is never the headline figure.
     */
    const sources = ref<RawSnapshotSource[]>([]);
    /** License gating + upsell state from the `access` block of GET /footprint. */
    const access = ref<FootprintAccessInfo | null>(null);
    /**
     * The plan's dark web monitoring (`monitoring.dark_web`) is on for this person, by the options
     * read's `enabled`: their plan carries it and their own switch is on. It is the one watch that
     * carries on once a scan is over, so it is the only one this page may name. False until that
     * read says otherwise, and the read is asked for only at the close (`loadMonitoring`).
     */
    const darkWebMonitoring = ref(false);
    /** This session has asked for that read. A session asks once, whether or not it is answered. */
    let monitoringRequested = false;
    /**
     * The descent has not been started, so the page is showing the briefing rather than a
     * result. True from the first paint and cleared only by a snapshot we can actually show
     * or a scan the person asked for: opening a page is not a request to spend the shared
     * search allowance, and a briefing that appears AFTER a scan has already run explains
     * something the reader has already watched happen.
     */
    const awaitingStart = ref(true);

    /**
     * Wave state, session-only. Values seen in a previous wave and NOT added to the
     * vault are hidden from deeper waves, so each wave surfaces what is new. Being
     * hidden is not a rejection: it never reaches the backend and never becomes a vault
     * exclusion. Only an explicit `excluded` disposition is sent, as a negative.
     * Reassigned (never mutated) so computeds track.
     */
    const waveHidden = ref<ReadonlySet<string>>(new Set());
    /**
     * What the person decided about each fragment this session, keyed by value id. Only
     * `excluded` leaves the browser, as a negative that narrows the next wave. Ownership,
     * familiarity and family are session notes; saving a vault identifier is a separate action.
     * Reassigned rather than mutated so the counters track.
     */
    const dispositions = ref<Readonly<Record<string, FragmentDisposition>>>({});
    /**
     * What each decided fragment actually WAS, captured at the moment it was decided.
     *
     * The view model cannot answer this later: descending hides the whole finished level, so
     * by the next request the decided value is no longer in `allValues`. Resolving a
     * disposition through it silently drops the rejection, the negative never travels, and the
     * source is paid to return the exact records the person already ruled out. The same gap
     * would leave retained review rows without the values they describe.
     *
     * `dispositions` stays the authority for WHAT was decided; this carries the words, so the
     * two are written together and never derived from each other.
     */
    const decisions = ref<Readonly<Record<string, FragmentDecision>>>({});
    /**
     * Fragments whose action is IN FLIGHT: a vault hand-off waiting on its form, or a family
     * attach waiting on the contacts write.
     *
     * A press opens a dialog after an await, and a second press lands inside that gap: two
     * hand-offs stack two vault forms over one value, and two attaches race two replaces of one
     * contact's email list. The row reads this to stop offering the press, and the handler reads
     * it to refuse one that arrives before the render catches up.
     *
     * Reassigned rather than mutated so the rows track it.
     */
    const actions = shallowRef<ReadonlyMap<string, symbol>>(new Map());
    const actingIds = computed<ReadonlySet<string>>(() => new Set(actions.value.keys()));

    /**
     * Whether the four actions have been explained once this descent. The dialog opens itself
     * the first time the reader is asked for a call and never again: an explainer that reappears
     * on every level is a thing to dismiss rather than a thing to read.
     */
    const explainerShown = ref(false);
    /** Value keys present before the current deeper wave, for the "new find" highlight. */
    let previousWaveKeys = new Set<string>();
    /**
     * The hide set a descent has asked for, held back until a snapshot from the deeper pass
     * actually arrives.
     *
     * It cannot be committed when the descent is requested: the poll re-reads the CURRENT
     * snapshot while the deeper pass is still running, and a committed hide set empties that
     * snapshot in the view. A pass that then times out or fails leaves the reader looking at
     * a footprint with nothing in it and no scan running to explain why.
     */
    let stagedHidden: { keys: Set<string>; belowDepth: number } | null = null;

    /** Live status-message text mirrored into an aria-live region (announced, never steals focus). */
    const statusMessage = ref('');

    const drawer = ref<{
        open: boolean;
        loading: boolean;
        /** A further metered page is loading behind the list already shown. */
        loadingMore: boolean;
        value: FootprintValue | null;
        fieldLabel: string;
        records: LeakedRecord[];
        /** Continuation for the next metered page; null when the list is complete. */
        nextCursor: string | null;
        /**
         * Why the live record view is unavailable — rendered instead of a silent empty
         * list, and distinct from a value that genuinely has no records.
         */
        error: DrawerRecordsError | null;
        /**
         * Why the NEXT page could not be fetched. Kept apart from `error`, which means
         * "there is nothing on screen at all": a page that did not arrive must not take
         * away the records already in front of the reader.
         */
        loadMoreError: DrawerRecordsError | null;
        /** Reported refusal metadata; source-failure retention is never a retry restriction. */
        retryAfter: number | null;
        /** A reveal for the open credential is in flight. */
        revealing: boolean;
        /**
         * The cleartext behind the open credential, once the reader explicitly asked for it.
         * It lives here and nowhere else, for as long as the drawer holds this value.
         */
        revealed: string | null;
        /** Why the reveal could not produce a value, surfaced inline rather than swallowed. */
        revealError: SensitiveRevealError | null;
    }>({
        open: false,
        loading: false,
        loadingMore: false,
        value: null,
        fieldLabel: '',
        records: [],
        nextCursor: null,
        error: null,
        loadMoreError: null,
        retryAfter: null,
        revealing: false,
        revealed: null,
        revealError: null,
    });

    /** Delay between scan-status polls (live); tests set this to 0. */
    const pollIntervalMs = ref(2000);

    let personId = 0;

    /**
     * Monotonic token identifying the load currently allowed to write state. Every read
     * that could outlive its trigger — a poll, a deeper-scan poll — captures it and
     * discards its result if a newer load has started. Without it, switching person
     * mid-scan lands person A's response under person B's profile.
     */
    let requestToken = 0;

    /** Start a new load generation, superseding anything already in flight. */
    function beginRequest(id: number): number {
        personId = id;

        return ++requestToken;
    }

    const isCurrent = (token: number) => {
        if (token !== requestToken) return false;
        checkSession();
        return token === requestToken;
    };

    /**
     * Identifies the PERSON context, not the request. Vault adds and drawer reads bind
     * to this rather than to `requestToken`, which every load and every deeper scan
     * increments — binding to that would orphan perfectly valid same-person work.
     */
    let personGeneration = 0;
    const isSamePerson = (generation: number) => generation === personGeneration;

    let sessionGeneration = 0;

    /** Scan-local decisions survive refreshes and deeper waves, but never a new session. */
    function captureSession(): () => boolean {
        const generation = sessionGeneration;

        return () => {
            if (generation !== sessionGeneration) return false;
            checkSession();
            return generation === sessionGeneration;
        };
    }

    /** Identifies the drawer open right now, so an earlier one's records can't land in it. */
    let drawerToken = 0;

    /**
     * Drop everything a pass produced. This page keeps no history, so it runs on every arrival
     * as well as on a person switch: a revisit that paints the previous visit's rows, or wears
     * its level mark, is the page claiming a search this visit has not run.
     */
    function clearResultState() {
        sessionGeneration++;
        applyDeferral('none', null, null);
        scanSession.value = null;
        sessionDeadline.value = null;
        sessionExtending.value = false;
        sessionError.value = '';
        closeDrawer();
        footprint.value = { groups: [] };
        meta.value = { ...emptyMeta(), person_name: meta.value.person_name };
        sources.value = [];
        truncated.value = false;
        noUsableSeeds.value = false;
        // Depth is the mark of the pass that produced what was on screen, so carrying it
        // forward puts a level on a visit that has searched nothing.
        depth.value = 'quick';
        waveHidden.value = new Set();
        dispositions.value = {};
        decisions.value = {};
        actions.value = new Map();
        explainerShown.value = false;
        previousWaveKeys = new Set();
        stagedHidden = null;
        // Whether monitoring is on can change between sessions, so each session asks for
        // itself, and a read still in flight for the session ending lands nowhere.
        darkWebMonitoring.value = false;
        monitoringRequested = false;
        // Toasts and their Undo callbacks belong to the session whose choices they describe.
        useFootprintToasts().clear();
        for (const close of [...sessionEndCallbacks]) close();
    }

    /**
     * Drop everything belonging to the person we were showing. Nothing here is shared
     * between people, so anything left behind is another person's data on screen.
     */
    function clearPersonState() {
        personGeneration++; // everything in flight now belongs to a person we have left
        clearResultState();
        // The name is theirs too, so it goes with the rest of their result state.
        meta.value = emptyMeta();
        sessionNotice.value = '';
        seeds.value = [];
        access.value = null;
        awaitingStart.value = true;
        // Status describes the person being left, and carrying it into the next one puts
        // their scan state on somebody else's page.
        status.value = 'idle';
        drawer.value = {
            open: false,
            loading: false,
            loadingMore: false,
            value: null,
            fieldLabel: '',
            records: [],
            nextCursor: null,
            error: null,
            loadMoreError: null,
            retryAfter: null,
            revealing: false,
            revealed: null,
            revealError: null,
        };
    }

    // ---- Getters -----------------------------------------------------------
    const allValues = computed<FootprintValue[]>(() => footprint.value.groups.flatMap((g) => g.fields.flatMap((f) => f.values)));
    const inVaultCount = computed(() => allValues.value.filter((v) => v.in_vault && !v.sensitive).length);
    /**
     * Distinct leaked records behind the footprint, from the server's authoritative
     * totals. Summing each VALUE's record_count would count one record once per field
     * it carries, inflating the headline number several-fold.
     */
    const recordsFound = computed(() => meta.value.record_count);
    const credentialsCount = computed(() => allValues.value.filter((v) => v.sensitive).length);
    const sourceCount = computed(() => meta.value.source_count);
    const hasFootprint = computed(() => footprint.value.groups.length > 0);
    const isScanning = computed(() => status.value === 'scanning');
    const isDeferred = computed(() => status.value === 'deferred');
    const isSourceUnavailable = computed(() => isDeferred.value && deferralReason.value === 'source_unavailable');
    const isError = computed(() => status.value === 'error');
    /** The read loop gave up while the scan was still running — results may be partial. */
    const isTimedOut = computed(() => status.value === 'timeout');
    /**
     * The pass this session started left nothing behind. Nothing is stored, so there is no
     * result to read back: only a fresh scan can produce one.
     */
    const isExpired = computed(() => status.value === 'expired');
    /**
     * The sweep settled and produced a result, so what is on screen is the whole answer. Every
     * other status is a sweep still running, or one that timed out, failed, was held back or lost
     * its snapshot, and none of those has produced a whole answer. The briefing is `idle` as well,
     * before any sweep has run, so this speaks for a result on screen, never for the briefing.
     */
    const isSettled = computed(() => status.value === 'idle');
    const canDeepen = computed(() => DEPTH_ORDER.indexOf(depth.value) < DEPTH_ORDER.length - 1);

    // ---- License gating / upsell getters -----------------------------------
    const tier = computed(() => access.value?.tier ?? null);
    /** Below Signature → surface the human-analyst Threat Hunter upsell. */
    const showThreatHunter = computed(() => access.value?.threat_hunter_available === true);
    /** The credential rows carry each value itself, so nothing is covered and nothing is revealed. */
    const credentialsInFull = computed(() => access.value?.credentials_in_full === true);

    /**
     * The fragments this level is asking the person to sort: everything on screen that is
     * not already held in the vault. A sensitive fragment has no value to decide about, so
     * it is evidence rather than a question and never joins the count.
     */
    const levelFragments = computed(() =>
        allValues.value.filter((value) => !value.sensitive && !value.in_vault)
    );

    /** How many still need a decision before the next level unlocks. */
    const undecidedCount = computed(
        () => levelFragments.value.filter((value) => !dispositions.value[value.id]).length
    );

    /**
     * Every decision this run has produced. Dispositions survive a descent on purpose, so
     * these are whole-run figures and lists: the foot counts what the person has sorted since
     * the descent began, not what is on the level in front of them.
     */
    const decisionList = computed(() => Object.values(decisions.value));

    /** Fragments confirmed as the person's own, independently of vault membership. */
    const meList = computed(() => decisionList.value.filter((decision) => decision.disposition === 'me').sort(byKindThenValue));
    const meCount = computed(() => meList.value.length);

    /** Fragments the person recognised. It never leaves the browser, so this is the only place it is counted. */
    const familiarCount = computed(() => decisionList.value.filter((decision) => decision.disposition === 'familiar').length);

    /** Fragments the person ruled out. Each one rides every later wave of this run as a negative. */
    const excludedCount = computed(() => decisionList.value.filter((decision) => decision.disposition === 'excluded').length);

    /**
     * The owned identifiers this scan goes out with, counted as FIELDS rather than as seeds: a
     * name is one seed carrying two fields and an address one carrying four, so counting seeds
     * would under-report what the vault is actually searching on.
     */
    const vaultSeedCount = computed(() => seeds.value.reduce((total, seed) => total + seed.fields.length, 0));

    /**
     * Fragments handed to a family member. Session-scoped, and it never travels as a negative.
     *
     * A family decision IS its member: the attribution is what the row names it by and what
     * the reader undoes it back to. `setFamily`/`setFamilyFor` are the only doors that can write
     * one — the disposition type keeps the others out — and the member is read here as well, so
     * a decision that somehow arrived without one is not a row nothing can name.
     */
    const familyList = computed(() =>
        decisionList.value.filter((decision) => decision.disposition === 'family' && decision.family).sort(byKindThenValue)
    );

    const familyCount = computed(() => familyList.value.length);

    /**
     * What this scan found that the vault already holds. It is the rail's list, and it is the
     * exact complement of what the main table shows: one value is a question in one place or a
     * settled fact in the other, never both. A credential is never settled by the vault.
     */
    const vaultFoundList = computed(() =>
        allValues.value.filter((value) => value.in_vault && !value.sensitive).sort(byKindThenValue)
    );

    /** Every fragment at this level has been sorted one way or the other. */
    const levelSorted = computed(() => levelFragments.value.length > 0 && undecidedCount.value === 0);

    /**
     * The deepest wave this licence may ask for, straight from the backend. Falls back to
     * the baseline rather than guessing from a slug, so an unknown plan is never handed a
     * level it has not paid for.
     */
    const maxDepth = computed(() => access.value?.max_depth ?? 1);

    /** At least one owned identifier is precise enough to search with. */
    const hasSearchableSeed = computed(() => seeds.value.some((seed) => seed.fields.length > 0));

    // ---- Internals ---------------------------------------------------------
    function announce(message: string) {
        statusMessage.value = message;
    }

    function locate(value: FootprintValue): { group: FootprintGroup; fieldKey: string; fieldLabel: string } | null {
        for (const group of footprint.value.groups) {
            for (const field of group.fields) {
                if (field.values.some((v) => v === value || v.id === value.id)) {
                    return { group, fieldKey: field.key, fieldLabel: field.label };
                }
            }
        }
        return null;
    }

    /**
     * This value can be handed to a family member. A value the vault already holds is the
     * reader's own by the backend's own word, so it is never a question of whose it is.
     */
    const isFamilyAddable = (value: { field_type: string; sensitive?: boolean; in_vault?: boolean }) =>
        !value.sensitive && !value.in_vault && FAMILY_FIELDS.has(value.field_type);

    /**
     * Fragments the person rejected, in the shape the scan endpoint negates on. Credentials
     * only accept ownership notes and never become search terms or exclusions.
     */
    function negativeFields(): SeedField[] {
        const inVault = new Set(allValues.value.filter((value) => value.in_vault).map((value) => value.id));

        return decisionList.value
            .filter((decision) => decision.disposition === 'excluded' && !inVault.has(decision.id))
            .map((decision) => ({ field_type: decision.field_type, value: decision.normalized_value }));
    }

    // ---- Live mapping (raw snapshot rows → curated view model) ---------------
    function mapValue(v: RawSnapshotValue): FootprintValue {
        const id = `${v.field_type}|${v.normalized_value}`;

        return {
            id,
            field_type: v.field_type,
            value: v.normalized_value,
            display: v.display_value || v.normalized_value,
            record_count: v.record_count,
            source_count: v.source_count,
            in_vault: v.in_vault,
            sensitive: false,
            is_new: previousWaveKeys.size > 0 && !previousWaveKeys.has(id),
        };
    }

    function buildGroupsFromRaw(raw: RawSnapshot): FootprintGroup[] {
        const byType = new Map<string, RawSnapshotValue[]>();
        for (const v of raw.values ?? []) {
            // The wave filter: a value seen in an earlier wave and not added is out of
            // THIS session's deeper view — but a vault add always stays visible with
            // its check mark, whichever wave surfaced it.
            const key = `${v.field_type}|${v.normalized_value}`;
            if (waveHidden.value.has(key) && !v.in_vault) continue;

            const list = byType.get(v.field_type) ?? [];
            list.push(v);
            byType.set(v.field_type, list);
        }

        const groups: FootprintGroup[] = [];
        for (const def of GROUP_DEFS) {
            const fields = def.fields
                .map((key) => ({ key, label: fieldKind(key).label, values: (byType.get(key) ?? []).map(mapValue) }))
                .filter((field) => field.values.length > 0);
            if (fields.length) groups.push({ id: def.id, name: def.name, fields });
        }

        const sensitive = raw.sensitive ?? [];
        if (sensitive.length) {
            groups.push({
                id: 'credentials',
                name: 'Exposed credentials',
                sensitive: true,
                fields: sensitiveFields(sensitive),
            });
        }
        return groups;
    }

    /**
     * One field per credential type, each carrying one masked value per distinct exposed
     * credential. Two similar passwords are two fragments, told apart by the server's mask
     * and by their own counts: collapsing them would hide the second exposure entirely.
     */
    function sensitiveFields(rows: RawSnapshotSensitive[]): FootprintField[] {
        const byType = new Map<string, RawSnapshotSensitive[]>();

        for (const row of rows) {
            const list = byType.get(row.field_type) ?? [];
            list.push(row);
            byType.set(row.field_type, list);
        }

        return [...byType].map(([fieldType, typeRows]) => ({
            key: fieldType,
            label: fieldKind(fieldType).label,
            values: typeRows.map((row, index) => {
                // The server's mask is the only form of this value the read path carries, except
                // to staff cleared to read credentials in full, who get the value as stored. A
                // reader who may not scan gets neither mask nor handle, and the fallback is what
                // keeps that row a visible presence instead of a blank cell.
                const masked = row.display_value || SENSITIVE_PLACEHOLDER[row.field_type] || '••••';
                // The handle is the identity where there is one. Without it there is nothing
                // stable to key on, so the row's position in its own type stands in: it is
                // never revealable, and ownership notes on it are cleared with each new snapshot.
                const id = `${row.field_type}|${row.key ?? `presence-${index}`}`;

                return {
                    id,
                    field_type: row.field_type,
                    value: masked,
                    display: masked,
                    record_count: row.record_count,
                    source_count: row.source_count,
                    in_vault: false,
                    sensitive: true,
                    // Absent for a reader who may not scan. Everything downstream reads this
                    // as "not revealable", which is exactly what it means.
                    sensitive_key: row.key || undefined,
                    // How the source stored it, never the value. Absent reads as null, and null
                    // as plain text: a hash changes what the value is called, never who may see it.
                    password_class: row.password_class ?? null,
                    // A credential surfaced by a deeper pass is a new find like any other,
                    // and the highlight is the only thing that says a deeper wave paid off.
                    is_new: previousWaveKeys.size > 0 && !previousWaveKeys.has(id),
                };
            }),
        }));
    }

    /** Fold a live GET /footprint snapshot into the view model. */
    function applyShowData(raw: RawSnapshot | null) {
        // Presence-only credential IDs are positions, so a new snapshot cannot inherit their notes.
        for (const value of allValues.value) {
            if (value.sensitive && !value.sensitive_key && dispositions.value[value.id] === 'me') {
                writeDecision(value.id, null, null);
            }
        }

        if (!raw) {
            footprint.value = { groups: [] };
            depth.value = 'quick';
            meta.value = { ...emptyMeta(), person_name: meta.value.person_name };
            sources.value = [];
            return;
        }

        // The staged hide set applies to snapshots from the pass it was staged for, and to
        // nothing shallower: a re-read of the level the reader is still standing on must
        // keep showing that level.
        if (stagedHidden !== null && raw.depth > stagedHidden.belowDepth) {
            waveHidden.value = new Set([...waveHidden.value, ...stagedHidden.keys]);
            stagedHidden = null;
        }

        const groups = buildGroupsFromRaw(raw);
        const nextDepth = depthFromInt(raw.depth);

        footprint.value = { groups };
        sources.value = raw.sources ?? [];

        // An open drawer holds a reference into the ROWS JUST REPLACED; left alone it
        // keeps rendering pre-refresh counts and vault state. Rebind it to the fresh
        // row by id — and close it when the value no longer exists in this wave's
        // view, rather than presenting a filtered value as still live.
        if (drawer.value.open && drawer.value.value) {
            const staleId = drawer.value.value.id;
            const live = groups.flatMap((g) => g.fields.flatMap((f) => f.values)).find((v) => v.id === staleId);

            if (live) drawer.value.value = live;
            else closeDrawer();
        }

        depth.value = nextDepth;
        meta.value = {
            person_name: meta.value.person_name,
            source_count: (raw.sources ?? []).length,
            // Per-source totals: a record belongs to exactly one source, so this is the
            // distinct record count. Folded-in verified breaches carry 0 and don't skew it.
            record_count: (raw.sources ?? []).reduce((sum, source) => sum + (source.record_count ?? 0), 0),
            scanned_at: raw.scanned_at,
            // A snapshot no pass produced carries no duration, and neither does one
            // serialized without the field. Both mean nothing was timed, never zero.
            duration_ms: raw.duration_ms ?? null,
            depth: nextDepth,
            pass: raw.depth || 1,
        };
    }

    /**
     * Records into the view model, with the catalogue's description of each dataset JOINED ON
     * rather than held beside them. The response keys descriptions by source to send each one
     * once; a record without its own is a card that has to reach back into drawer state to say
     * what it came out of, and the two go out of step the moment a page arrives.
     */
    function mapRecords(records: RawLeakedRecord[], datasets: Record<string, DatasetMeta | null> = {}): LeakedRecord[] {
        return (records ?? []).map((r, i) => ({
            id: r.record_id || `rec-${i}`,
            source_key: slug(r.source || 'source'),
            source_name: r.source || 'Unknown source',
            dataset: r.dataset || '',
            line: typeof r.line === 'number' ? r.line : null,
            logo: (r.source?.trim()?.[0] ?? '?').toUpperCase(),
            // The endpoint computes this per record against the person's verified breaches.
            verified: !!r.verified,
            // Own keys only: a source named `constructor` would otherwise read a prototype
            // member and hand a card an object that is not a description.
            meta: Object.hasOwn(datasets, r.source) ? (datasets[r.source] ?? null) : null,
            password_class: r.password_class ?? null,
            password_key: r.password_key ?? null,
            fields: (r.fields ?? []).map((f) => ({
                // The server's normalized spelling is what makes this field the SAME fragment
                // as the row the reader opened the drawer from. The client never derives it.
                id: `${f.field_type}|${f.normalized_value}`,
                key: f.field_type,
                label: fieldKind(f.field_type).label,
                value: f.value,
                normalized_value: isSensitiveType(f.field_type) ? '' : f.normalized_value,
                sensitive: isSensitiveType(f.field_type),
                // The record and the fragment can come from separate reads, so fragmentState
                // accepts vault membership reported by either response.
                in_vault: !!f.in_vault,
            })),
        }));
    }

    /**
     * One GET /footprint read: stores access, folds in the snapshot, returns the scan
     * status. Returns `stale` — writing nothing — when a newer load has superseded this
     * one, so a slow response for a previous person can never land on the current one.
     */
    async function fetchShowOnce(
        id: number,
        token: number,
        options: { applyBody?: boolean } = {}
    ): Promise<'scanning' | 'ready' | 'deferred' | 'none' | 'error' | 'stale'> {
        const nuxtApp = useNuxtApp();
        const requestStartedAt = Date.now();
        const res = await usePbApi<FootprintShowResponse>(nuxtApp.$api.routes.people.footprint.show(id));

        if (!isCurrent(token)) return 'stale';
        if (res.status !== 200 || !res.data) return 'error';

        // Depth zero is verified breach history, not a temporary scan result.
        const snapshot = res.data.data && res.data.data.depth > 0 ? res.data.data : null;
        if (options.applyBody !== false) {
            if (snapshot && !res.data.session) {
                endSession('ended');
                return 'stale';
            }
            if (!applySession(res.data.session, requestStartedAt)) return 'stale';
        }
        access.value = res.data.access ?? null;
        applyDeferral(res.data.status, res.data.reason, res.data.retry_after);

        // A wave in flight WITHHOLDS the body of the wave it supersedes: `scanning` with no
        // body carries zeroed totals and reports a pass, never an empty footprint. Applying it
        // would strip the level the reader is standing on, drop its headline counts to zero and
        // mark the page back at level 1 until the deeper pass lands. The body is not gone —
        // it returns when this pass ends, under `deferred` if it aborts.
        if (res.data.status === 'scanning' && !snapshot) return 'scanning';

        // The scanner presents only this visit's scan. Baseline sources and their metadata
        // remain available to other API consumers without becoming results in this view.
        if (res.data.data && !snapshot) return res.data.status;

        // Whether the server bounded what it sent back: a display bound, not a scan state.
        // The two caps fold into one flag because the field they bound is one flat list and
        // the note it drives makes one claim, that this view is bounded. Splitting them
        // would put two notes in one foot over a distinction no reader can act on.
        truncated.value = !!res.data.values_truncated || !!res.data.sensitive_truncated;

        // The arrival read wants the entitlement and the scan status, never the body: a
        // finished pass left by an earlier visit is not a result to show. This page keeps
        // no history, so every visit runs its own scan or shows nothing at all.
        if (options.applyBody === false) return res.data.status;

        // Read here rather than above, for the same reason: this is a verdict on a pass, and an
        // earlier visit's terminal state must not be painted onto this visit's briefing.
        noUsableSeeds.value = !!res.data.no_usable_seeds;

        applyShowData(snapshot);

        // The server's whole-snapshot totals outrank anything derivable from the
        // (possibly capped) source list — headline numbers must not shrink because
        // a list was bounded for transport.
        if (res.data.totals) {
            meta.value.source_count = res.data.totals.sources;
            meta.value.record_count = res.data.totals.records;
        }

        return res.data.status;
    }

    /**
     * Ask the backend to run a wave: depth plus the session's switched-off seeds.
     * A 402 is the Pro upsell and a 403 is the scan gate refusing this reader on this
     * profile. Neither is an error.
     */
    async function requestScan(
        id: number,
        depthInt: number,
        token: number
    ): Promise<'scanning' | 'deferred' | 'upgrade_required' | 'forbidden' | 'error' | 'stale'> {
        const nuxtApp = useNuxtApp();
        const res = await usePbApi<ScanRequestResponse & { error?: string }>(nuxtApp.$api.routes.people.footprint.scan(id), {
            method: 'POST',
            body: { depth: depthInt, negatives: negativeFields() },
        });

        if (!isCurrent(token)) return 'stale';
        if (res.status === 402) return 'upgrade_required';
        if (res.status === 403) return 'forbidden';
        if ((res.status !== 200 && res.status !== 202) || !res.data?.data) return 'error';

        applyDeferral(res.data.data.status, res.data.data.reason, res.data.data.retry_after);
        return res.data.data.status;
    }

    /** Read until the backend settles, the poll cap is reached, or a newer load supersedes us. */
    async function pollUntilSettled(id: number, token: number): Promise<'scanning' | 'ready' | 'deferred' | 'none' | 'error' | 'stale'> {
        let scan = await fetchShowOnce(id, token);
        let polls = 0;

        while (scan === 'scanning' && polls++ < MAX_POLLS) {
            await sleep(pollIntervalMs.value);
            if (!isCurrent(token)) return 'stale';
            scan = await fetchShowOnce(id, token);
        }

        return scan;
    }

    /**
     * A scan still reporting `scanning` at the poll cap has NOT completed. Reporting it
     * as idle would announce a finished search over empty or partial data and stop polling.
     * `deferred` preserves a refused or failed pass rather than declaring it complete.
     *
     * This runs only after a pass this session started, and a completed pass always leaves
     * something behind — even the terminal "nothing searchable" outcome stores an empty
     * payload — so `none` here means the snapshot is gone, never that the scan found nothing.
     */
    function settledStatus(scan: 'scanning' | 'ready' | 'deferred' | 'none' | 'error'): ScanStatus {
        if (scan === 'error') return 'error';
        if (scan === 'deferred') return 'deferred';
        if (scan === 'none') return 'expired';

        return scan === 'scanning' ? 'timeout' : 'idle';
    }

    function announceSettled() {
        if (status.value === 'error') {
            announce('We couldn’t refresh your results. What is on screen is what this scan found.');
        } else if (status.value === 'timeout') {
            announce('This scan is taking longer than usual. It’s still running. Check for results when you’re ready.');
        } else if (status.value === 'deferred') {
            announce(deferralNotice.value);
        } else if (status.value === 'expired') {
            endSession('expired');
        } else if (noUsableSeeds.value) {
            // Nothing went out, so there is no all-clear to give: the counts would be a report
            // on a search that never ran.
            announce(
                'We had nothing precise enough to search with. Add a verified email, phone number or address to your Identity Vault, and the next scan goes out with it.'
            );
        } else {
            // The pass has settled with a result: say that the search finished, and what it found.
            announce(`Search finished. We found ${plural(recordsFound.value, 'record')} across ${plural(sourceCount.value, 'source')}.`);
        }
    }

    // ---- Actions -----------------------------------------------------------
    /**
     * Read scan status and join a running wave. Starting a new wave requires an explicit
     * action. The show payload carries no seeds (they ARE the person's owned identifiers),
     * so the page injects them from the person store.
     */
    async function loadFootprint(id: number, seedList?: Seed[]) {
        // A same-person load SUPERSEDES an active one rather than being dropped: the
        // token below strands the older poll loop harmlessly, and discarding the new
        // load would discard what triggered it — an in-place license upgrade needs
        // this read to refresh the access block, or the old tier's upsell survives
        // the purchase until a remount.

        const switchingContext = personId !== 0 && personId !== id;
        const token = beginRequest(id);

        if (switchingContext) clearPersonState();

        // Arrival keeps no history, so whatever an earlier pass produced goes before this visit
        // reads anything. Without this a revisit renders the previous visit's rows under the
        // briefing and wears its level mark.
        clearResultState();

        if (seedList) seeds.value = seedList;

        try {
            // The arrival read: entitlement and scan status only. It costs nothing, it
            // claims nothing, and it never paints a body.
            let scan = await fetchShowOnce(id, token, { applyBody: false });

            if (scan === 'stale') return;

            // A pass already running belongs to this person right now, so the page joins
            // it. Anything else, including a finished pass from an earlier visit, leaves
            // the briefing in place: beginDescent() is the only thing that starts a wave,
            // and the results it produces are the only ones this page will show.
            if (scan !== 'scanning') {
                awaitingStart.value = true;

                // A deferred status describes the latest scan outcome. Source failure
                // does not prohibit a new explicit ask; shared limits and disabled service
                // still refuse it. A failed status read cannot establish any of those
                // outcomes, so only a deferred response supplies the briefing notice.
                status.value = scan === 'deferred' ? 'deferred' : 'idle';
                announce(
                    scan === 'deferred'
                        ? deferralNotice.value
                        : 'Your scan is ready to start.'
                );
                return;
            }

            awaitingStart.value = false;
            status.value = 'scanning';
            announce('A scan is already running. Watching for what it finds…');

            scan = await pollUntilSettled(id, token);

            if (scan === 'stale') return; // superseded by a newer load — write nothing
            status.value = settledStatus(scan);

            announceSettled();
        } catch {
            if (!isCurrent(token)) return;

            // Same rule as the arrival read's own error branch: a read that throws before a
            // scan has run is not a failed scan, and dressing the briefing in an error the
            // reader cannot act on makes a working page look broken.
            if (awaitingStart.value) return;

            // Keep this session's own result visible with a non-blocking notice.
            status.value = 'error';
            announce('We couldn’t refresh your results. What is on screen is what this scan found.');
        }
    }

    /**
     * Re-inject the person's owned identifiers mid-session.
     *
     * A vault add or an identifier edit changes what the NEXT wave may search with and nothing
     * else, so it never routes through loadFootprint: arrival deliberately refuses to paint a
     * body, and re-entering it would drop a reader who is sorting a live level back onto the
     * briefing and cost them a wave to get back.
     */
    function setSeeds(seedList: Seed[]) {
        seeds.value = seedList;
    }

    /**
     * Stop reading. Every in-flight read captured the token this moves, so the next poll writes
     * nothing and the loop unwinds within one interval instead of following a reader who has
     * left the page. Status is left exactly as it is: the server-side pass carries on, and this
     * only says nobody is watching it.
     *
     * It moves `requestToken` and NOT `personGeneration`: vault adds, drawer reads and reveals
     * bind to that one, and orphaning them would refuse work that is still perfectly valid.
     */
    function cancelPolling() {
        requestToken++;
    }

    /** Re-run the current (or a deeper) wave and poll the fresh snapshot in. */
    async function rerunWave(targetDepth: number): Promise<DeeperScanResult> {
        const id = personId;
        const token = beginRequest(id);
        status.value = 'scanning';
        applyDeferral('scanning', null, null);
        // The verdict belongs to the pass that produced it, and this one has not reported yet.
        noUsableSeeds.value = false;

        try {
            const requested = await requestScan(id, targetDepth, token);

            if (!isCurrent(token)) return 'ok';

            if (requested === 'upgrade_required') {
                status.value = 'idle';
                // Named from the level table, the same source the action bar's gate reads: level
                // 2 is reachable on several plans, so naming one of them sends a reader to a
                // page that is not the one they need, and level 3 tells a Pro customer to buy
                // what they already own.
                const refused = JOURNEY_LEVELS[targetDepth - 1];

                announce(
                    `${refused?.name ?? 'This level'} is part of ${refused?.plan ?? 'a paid plan'}. Upgrade to search your verified identifiers.`
                );
                return 'upgrade_required';
            }
            if (requested === 'forbidden') {
                // The write is the authority the read did not have: this reader may not scan
                // this profile. Holding that in the access image puts the refusal under the
                // start control, where the briefing already has the words for it, rather than
                // leaving a refusal to read as an outage. With no access image there is
                // nothing to correct, and minting one would publish entitlement figures the
                // backend never sent.
                if (access.value) access.value = { ...access.value, can_scan: false };
                status.value = 'idle';
                // Word for word what the briefing's own note says under the start control, so
                // the reader hears the same reason a sighted reader is looking at.
                announce('This account cannot run a scan on this profile.');
                return 'forbidden';
            }
            if (requested === 'error') {
                status.value = 'error';
                announce('We couldn’t run the scan. What is on screen is what this scan found.');
                return 'error';
            }
            if (requested === 'deferred') {
                status.value = 'deferred';
                announce(deferralNotice.value);
                return 'deferred';
            }

            const scan = await pollUntilSettled(id, token);
            if (scan === 'stale') return 'ok';

            status.value = settledStatus(scan);
            if (status.value === 'deferred') announceSettled();
            return status.value === 'error' ? 'error' : status.value === 'deferred' ? 'deferred' : 'ok';
        } catch {
            if (!isCurrent(token)) return 'ok';
            status.value = 'error';
            announce('We couldn’t run the scan. What is on screen is what this scan found.');
            return 'error';
        }
    }

    /**
     * Write both halves of one decision at once: what was decided, and what it was decided
     * about. They are reassigned rather than mutated so every counter and list tracks, and
     * they move together because a disposition whose words went missing is a decision that
     * cannot be shown, sent or undone.
     */
    function writeDecision(id: string, disposition: FragmentDisposition | null, entry: FragmentDecision | null) {
        const nextDispositions = { ...dispositions.value };
        const nextDecisions = { ...decisions.value };

        if (disposition === null) {
            delete nextDispositions[id];
            delete nextDecisions[id];
        } else {
            nextDispositions[id] = disposition;

            if (entry) nextDecisions[id] = entry;
            else delete nextDecisions[id];
        }

        dispositions.value = nextDispositions;
        decisions.value = nextDecisions;
    }

    /**
     * Record what the person decided about one fragment. Passing null clears it, which is
     * how an undo returns the fragment to the count. A fragment already in the vault is not
     * a question, so it takes no disposition.
     */
    function setDisposition(id: string, disposition: UnattributedDisposition | null): void {
        if (disposition === null) {
            writeDecision(id, null, null);
            return;
        }

        // Capture the original fragment before a deeper wave removes it from the view.
        const value = allValues.value.find((candidate) => candidate.id === id);
        if (value?.sensitive) {
            setDispositionFor({ id, field_type: value.field_type, value: '', normalized_value: '', sensitive: true }, disposition);
            return;
        }
        const entry = value && !value.sensitive ? decisionOf(id, value.field_type, value.display, value.value, disposition) : null;

        writeDecision(id, disposition, entry);
    }

    /**
     * The same decision, made from a RECORD field rather than from a live fragment row.
     *
     * The drawer can show a field the snapshot never carried: the values list is capped, and a
     * record drilled from one fragment carries every other field on the same line. Routing
     * those through `setDisposition` would find nothing in `allValues` and record a disposition
     * with no words behind it, which never travels as a negative and never appears in a list.
     * The field already knows its own identity and both its spellings, so it brings them.
     */
    function setDispositionFor(
        field: { id: string; field_type: string; value: string; normalized_value: string; sensitive?: boolean },
        disposition: UnattributedDisposition | null
    ): void {
        if (disposition === null) {
            writeDecision(field.id, null, null);
            return;
        }

        if (field.sensitive || isSensitiveType(field.field_type)) {
            // Credential ownership is a note about an opaque handle, never a copy of the secret.
            if (disposition !== 'me') return;
            writeDecision(field.id, 'me', {
                id: field.id,
                field_type: field.field_type,
                display: `Exposed ${fieldKind(field.field_type).label.toLowerCase()}`,
                normalized_value: '',
                disposition: 'me',
                family: null,
                sensitive: true,
            });
            return;
        }

        writeDecision(field.id, disposition, decisionOf(field.id, field.field_type, field.value, field.normalized_value, disposition));
    }

    /**
     * Attribute one live fragment to a family member.
     *
     * A separate entry point rather than a fourth argument to `setDisposition`, because the
     * member is not optional here: a `family` decision must name its member and preserve the
     * attribution that Undo restores.
     */
    function setFamily(id: string, contact: FamilyAttribution): void {
        const value = allValues.value.find((candidate) => candidate.id === id);
        if (value?.sensitive) return;
        const entry = value ? decisionOf(id, value.field_type, value.display, value.value, 'family', contact) : null;

        writeDecision(id, 'family', entry);
    }

    /**
     * Claim a fragment's action, or report that somebody already holds it. One call does both,
     * so a caller cannot check and act in two steps with the gap the double press lives in.
     */
    function beginAct(id: string): { isCurrent: () => boolean; release: () => void } | null {
        if (actions.value.has(id)) return null;

        const token = Symbol();
        actions.value = new Map(actions.value).set(id, token);
        const isCurrent = () => actions.value.get(id) === token;

        return { isCurrent, release: () => { if (isCurrent()) endAct(id); } };
    }

    /** Release the action currently held on this fragment. */
    function endAct(id: string): void {
        if (!actions.value.has(id)) return;

        const next = new Map(actions.value);
        next.delete(id);
        actions.value = next;
    }

    /** The explainer has had its one showing this descent. */
    function markExplainerShown(): void {
        explainerShown.value = true;
    }

    /** The same attribution, made from a record field the snapshot cap left out of the list. */
    function setFamilyFor(
        field: { id: string; field_type: string; value: string; normalized_value: string },
        contact: FamilyAttribution
    ): void {
        if (isSensitiveType(field.field_type)) return;
        const entry = decisionOf(field.id, field.field_type, field.value, field.normalized_value, 'family', contact);

        writeDecision(field.id, 'family', entry);
    }

    /**
     * Where one record field stands. The facts outrank the notes: a credential is evidence
     * rather than a question whatever else is true of it, and a value the vault holds is
     * settled regardless of anything decided about it earlier.
     *
     * The vault is read from the field AND from the live fragment, because they learn it at
     * different moments: the field carries what the server knew when the page was fetched,
     * and the live row can carry a more recent snapshot after the vault form saves.
     */
    function fragmentState(field: LeakedRecordField): FragmentState {
        if (field.sensitive) return 'credential';
        if (field.in_vault || allValues.value.some((value) => value.id === field.id && value.in_vault)) return 'vault';

        return dispositions.value[field.id] ?? 'undecided';
    }

    /**
     * Start the descent: the first level, asked for by the person rather than by the page.
     *
     * This is the ONLY thing that turns a visit into a search. Every wave spends from a
     * shared allowance, so it is spent when someone chooses to spend it, and the briefing
     * above it is what they read before choosing.
     */
    async function beginDescent(): Promise<DeeperScanResult> {
        const id = personId;

        if (!id || isScanning.value) return 'error';

        // can_scan is false for a reader the scan gate refuses: Privacy Bee staff, and a
        // company viewer without `write employees` (the admin who manages the seat may
        // start the member's scan). The control is disabled in that case, so this only
        // catches a caller that got here another way, and it reports the same refusal the
        // scan POST reports when the read never knew about the gate.
        if (access.value?.can_scan === false) return 'forbidden';

        if (!hasSearchableSeed.value) {
            announce('Add a verified email, phone number or address to your profile to run a scan.');
            return 'error';
        }

        // A run-through starts clean: nothing from an earlier pass may hide a fragment this one
        // turns up, and nothing it left may be painted as this one's result. The vault itself
        // (server-side in_vault) carries the marks.
        clearResultState();

        awaitingStart.value = false;
        announce('Starting your scan…');

        sessionNotice.value = '';
        const isCurrentSession = captureSession();
        const result = await rerunWave(1);
        if (!isCurrentSession()) return result;

        // A refused start leaves nothing on screen to stand on, so the briefing takes the
        // page back rather than stranding the reader on an empty level.
        if (result !== 'ok') {
            awaitingStart.value = true;
        } else if (!isScanning.value) {
            // Level 1 reports its outcome the way the deeper levels do, or the region is
            // left holding "Starting your scan…" for the rest of the session. Still
            // scanning means a newer load took the page over and will report for itself.
            announceSettled();
        }

        return result;
    }

    /**
     * Run the next, deeper wave on the person's OWNED verified identifiers. What this wave
     * showed and the person did NOT add is hidden from the deeper view (session state only,
     * never negated server-side and never a vault exclusion), so the next wave surfaces what
     * is new.
     */
    async function runDeeperScan(): Promise<DeeperScanResult> {
        // Descending means the level BELOW the one on screen, so with nothing on screen it
        // would ask for level 2 and skip level 1 entirely. Nothing offers the control in
        // that state; this is what keeps it true if something ever does.
        if (awaitingStart.value) return beginDescent();

        // A timeout may leave work running. Source failure permits an explicit retry;
        // quota and disabled-service refusals still prohibit this deeper action.
        if (isScanning.value || isTimedOut.value || (isDeferred.value && !isSourceUnavailable.value)) return 'ok';

        if (!hasSearchableSeed.value) {
            announce('Add a verified email, phone number or address to your profile to search deeper.');
            return 'error';
        }

        // This wave's not-added values leave the view for the deeper waves, and the
        // survivors seed the "new find" highlight on what the deeper pass surfaces.
        //
        // Neither is committed here. A refused descent (error, deferred, or a 402 from a
        // level past the licence) leaves the person on THIS level, and so does a poll that
        // re-reads the current snapshot while the deeper pass is still running: hiding this
        // level for either of those empties the footprint and reports a false all-clear.
        // Two different sets over the same wave. The highlight has to know about EVERY
        // fragment already seen, credentials included, or each re-read paints them as new
        // finds. Credential evidence stays visible even when ownership is confirmed, because
        // a self-identification does not resolve an exposed credential.
        const decidable = allValues.value.filter((v) => !v.sensitive);
        const previousWaveKeysBefore = previousWaveKeys;
        const currentDepth = DEPTH_ORDER.indexOf(depth.value) + 1;

        previousWaveKeys = new Set(allValues.value.map((v) => v.id));
        stagedHidden = {
            keys: new Set(decidable.filter((v) => !v.in_vault).map((v) => v.id)),
            belowDepth: currentDepth,
        };

        announce('Running a deeper scan on your verified identifiers…');

        const isCurrentSession = captureSession();
        const result = await rerunWave(Math.min(currentDepth + 1, DEPTH_ORDER.length));
        if (!isCurrentSession()) return result;

        if (result !== 'ok') {
            previousWaveKeys = previousWaveKeysBefore;
            stagedHidden = null;
        }

        // A pass that ran but did not settle into a result — the poll cap, or a snapshot that
        // is already gone — reports the state the page is now in. Left alone, the region holds
        // "Running a deeper scan…" over a page that has stopped running one. Still scanning
        // means a newer load took the page over and will report for itself.
        if (result === 'ok' && status.value !== 'idle' && !isScanning.value) {
            announceSettled();
        } else if (result === 'ok' && status.value === 'idle') {
            // Counted against the wave that just ran, never as a set-size delta: this wave hides
            // the previous one's unclaimed fragments, so the size of the list moves by (new
            // finds - hidden), and a cumulative ladder makes that <= 0 in the ordinary case.
            // The announcement would then say "nothing further" while new finds are on screen.
            const found = allValues.value.filter((v) => !previousWaveKeys.has(v.id)).length;
            // At the display cap the visible count cannot move even when the pass
            // found more, so "nothing further" would be a claim the bounded view
            // cannot support.
            announce(
                truncated.value
                    ? 'Deeper pass run. Your footprint shows the maximum number of values we can display.'
                    : found > 0
                      ? `Deeper pass run. We found ${found} more value${found === 1 ? '' : 's'} matching your verified identifiers.`
                      : 'Deeper pass run. Nothing further at your current identity.'
            );
        }

        return result;
    }

    /**
     * Open the drawer on a value and fetch the individual leaked records behind it
     * (live, session-scoped).
     *
     * Opening one value and then another before the first responds must not pair the
     * second value's header with the first value's leaked records, so each open claims
     * a generation and a late response for a superseded one is dropped.
     */
    async function openDrawer(value: FootprintValue) {
        const isLiveSession = captureSession();
        if (!isLiveSession()) return;
        const found = locate(value);
        const drawerGeneration = ++drawerToken;
        const openedFor = personGeneration;
        const id = personId;

        drawer.value = {
            open: true,
            loading: true,
            loadingMore: false,
            value,
            fieldLabel: found?.fieldLabel ?? '',
            records: [],
            nextCursor: null,
            error: null,
            loadMoreError: null,
            retryAfter: null,
            // Every open starts behind the gate. Carrying a previous credential's cleartext
            // into the next drawer would show one value under another value's heading.
            revealing: false,
            revealed: null,
            revealError: null,
        };

        const isCurrentDrawer = () => drawerGeneration === drawerToken && isSamePerson(openedFor) && isLiveSession();

        try {
            // A credential's evidence is the value itself, behind the drawer's reveal gate.
            // There is no record set to drill into, so no metered call is made for one.
            if (value.sensitive) {
                drawer.value.records = [];
                return;
            }
            const nuxtApp = useNuxtApp();
            // POST: each page is a metered live source call, so browser prefetch or a
            // transport-level GET retry must never be able to spend the allowance.
            const res = await usePbApi<FootprintRecordsResponse>(nuxtApp.$api.routes.people.footprint.records(id), {
                method: 'POST',
                body: { field_type: value.field_type, value: value.display },
            });

            if (!isCurrentDrawer()) return;
            if (expireIfReported(res.status, res.data)) return;

            // A refusal is information, never a silent empty list. 422 = the value is
            // too low-precision to search alone, 429 = the search allowance is spent,
            // 409/404 = the session snapshot expired or rotated past this value,
            // 503 = the source itself is down, anything else = the request failed.
            if (res.status === 200 && res.data?.data) {
                drawer.value.records = mapRecords(res.data.data.records, res.data.data.datasets);
                // The aggregate count in the header can exceed one page; the cursor is
                // what makes the rest reachable instead of silently absent.
                drawer.value.nextCursor = res.data.data.next_cursor ?? null;
            } else {
                drawer.value.records = [];
                drawer.value.error = recordsError(res.status, res.data?.reason);
                drawer.value.retryAfter = retryAfterSeconds(res.data);
            }
        } finally {
            if (isCurrentDrawer()) drawer.value.loading = false;
        }
    }

    /** Keep the reported window as metadata; source-failure retention never blocks an explicit retry. */
    function retryAfterSeconds(body: Pick<FootprintRecordsResponse, 'retry_after'> | null | undefined): number | null {
        return typeof body?.retry_after === 'number' && body.retry_after > 0 ? body.retry_after : null;
    }

    function expireIfReported(httpStatus: number, body: { error?: string } | null | undefined): boolean {
        if (httpStatus !== 409 || body?.error !== 'scan_expired') return false;
        endSession('expired');
        return true;
    }

    function recordsError(httpStatus: number, reason: FootprintRecordsResponse['reason']): DrawerRecordsError {
        if (httpStatus === 422) return 'not_drillable';
        if (httpStatus === 429) return 'quota_exhausted';
        if (httpStatus === 409 || httpStatus === 404) return 'scan_expired';
        // A 503 also carries disabled-service refusals, so the captured reason governs retry.
        if (httpStatus === 503 && (reason === 'disabled' || reason === 'source_unavailable')) return reason;

        return 'failed';
    }

    function closeDrawer() {
        // A close invalidates every drawer read in flight: a page landing afterwards would
        // repopulate a drawer the reader dismissed. The guarded `finally` blocks below stop
        // running once the token has moved, so their flags are cleared here instead.
        drawerToken++;
        drawer.value.open = false;
        drawer.value.loading = false;
        drawer.value.loadingMore = false;
        drawer.value.value = null;
        drawer.value.records = [];
        drawer.value.nextCursor = null;
        drawer.value.error = null;
        drawer.value.loadMoreError = null;
        drawer.value.retryAfter = null;
        // Closing drops the cleartext. It is held for one open drawer, never for the page.
        drawer.value.revealing = false;
        drawer.value.revealed = null;
        drawer.value.revealError = null;
    }

    /**
     * Fetch the next metered page of drawer records — an explicit user action, never
     * automatic, because every page spends the shared allowance.
     */
    async function loadMoreRecords() {
        const isLiveSession = captureSession();
        if (!isLiveSession()) return;
        const value = drawer.value.value;
        const cursor = drawer.value.nextCursor;

        if (!value || !cursor || drawer.value.loadingMore) return;

        const drawerGeneration = drawerToken;
        const openedFor = personGeneration;
        const id = personId;
        const isCurrentDrawer = () => drawerGeneration === drawerToken && isSamePerson(openedFor) && isLiveSession();

        drawer.value.loadingMore = true;
        // A fresh attempt is not still failing.
        drawer.value.loadMoreError = null;
        drawer.value.retryAfter = null;

        try {
            const nuxtApp = useNuxtApp();
            const res = await usePbApi<FootprintRecordsResponse>(nuxtApp.$api.routes.people.footprint.records(id), {
                method: 'POST',
                body: { field_type: value.field_type, value: value.display, cursor },
            });

            if (!isCurrentDrawer()) return;
            if (expireIfReported(res.status, res.data)) return;

            if (res.status === 200 && res.data?.data) {
                drawer.value.records = [...drawer.value.records, ...mapRecords(res.data.data.records, res.data.data.datasets)];
                drawer.value.nextCursor = res.data.data.next_cursor ?? null;
            } else {
                // The records already on screen are untouched by a page that did not arrive,
                // and the cursor still points at it, so the failure is reported beside the
                // control that retries it rather than in place of the list.
                drawer.value.loadMoreError = recordsError(res.status, res.data?.reason);
                drawer.value.retryAfter = retryAfterSeconds(res.data);
            }
        } finally {
            if (isCurrentDrawer()) drawer.value.loadingMore = false;
        }
    }

    /**
     * Fetch the cleartext behind ONE masked credential, on the reader's explicit ask.
     *
     * The result is held in the open drawer and nowhere else: it is never written to
     * storage, never copied onto another value, and it is dropped the moment the drawer
     * closes or moves on. A reveal that lands after the drawer moved on is discarded for
     * the same reason a late record page is.
     */
    async function revealSensitive(value: FootprintValue): Promise<SensitiveRevealResult> {
        const isLiveSession = captureSession();
        if (!isLiveSession()) return { ok: false, reason: 'scan_expired' };
        if (!value.sensitive || !value.sensitive_key || drawer.value.revealing) {
            return { ok: false, reason: 'value_not_found' };
        }

        const drawerGeneration = drawerToken;
        const openedFor = personGeneration;
        const id = personId;
        const isCurrentDrawer = () =>
            drawerGeneration === drawerToken && isSamePerson(openedFor) && isLiveSession() && drawer.value.value?.id === value.id;

        drawer.value.revealing = true;
        drawer.value.revealError = null;

        try {
            const nuxtApp = useNuxtApp();
            // POST: the cleartext must never sit in a URL, a prefetch, or a history entry.
            const res = await usePbApi<SensitiveRevealResponse>(nuxtApp.$api.routes.people.footprint.sensitiveReveal(id), {
                method: 'POST',
                body: { field_type: value.field_type, key: value.sensitive_key },
            });

            if (!isCurrentDrawer()) return { ok: false, reason: 'failed' };
            if (expireIfReported(res.status, res.data)) return { ok: false, reason: 'scan_expired' };

            if (res.status === 200 && res.data?.data?.value) {
                drawer.value.revealed = res.data.data.value;

                return { ok: true, value: res.data.data.value };
            }

            const reason = revealError(res.status, res.data);
            drawer.value.revealError = reason;

            return { ok: false, reason };
        } finally {
            if (isCurrentDrawer()) drawer.value.revealing = false;
        }
    }

    /**
     * 409 = the snapshot is gone; 404 = the snapshot no longer carries this handle; 403
     * `reveal_self_only` = this reader is not the person (nor a minor's guardian). Read by
     * its code, not its status: any other 403 is a refusal this page has no words for.
     */
    function revealError(httpStatus: number, body: { error?: string } | null | undefined): SensitiveRevealError {
        if (httpStatus === 409) return 'scan_expired';
        if (httpStatus === 404) return 'value_not_found';
        if (httpStatus === 403 && body?.error === 'reveal_self_only') return 'self_only';

        return 'failed';
    }

    /**
     * Re-read the footprint for the pass this session is already watching. The poll loop
     * stops at its cap, so a scan still running past it needs an explicit read to pick up
     * where it got to: nothing resumes polling on its own.
     *
     * NOT loadFootprint. That is the arrival path, and arrival deliberately refuses to paint
     * a body, so routing a refresh through it would throw away the results the reader is
     * looking at and drop them back on the briefing. This re-read belongs to a pass they
     * started, which is exactly the case where a body IS theirs to see.
     */
    async function refresh(): Promise<void> {
        const id = personId;

        if (!id || awaitingStart.value) return;

        const token = beginRequest(id);
        status.value = 'scanning';
        announce('Checking for what the scan has found so far…');

        try {
            let scan = await fetchShowOnce(id, token);

            if (scan === 'stale') return;
            if (scan === 'scanning') scan = await pollUntilSettled(id, token);
            if (scan === 'stale') return;

            status.value = settledStatus(scan);
            announceSettled();
        } catch {
            if (!isCurrent(token)) return;
            status.value = 'error';
            announce('We couldn’t refresh your results. What is on screen is what this scan found.');
        }
    }

    /**
     * Ask whether dark web monitoring is on, for the close at the last level. Once per session:
     * a visit that never reaches the close never asks, and a close that comes back does not ask
     * again. Nothing waits on the answer, and one that lands after its session has ended is
     * dropped, so it can never reach another person or another session's close.
     */
    async function loadMonitoring(): Promise<void> {
        if (monitoringRequested || !personId) return;
        monitoringRequested = true;

        const isCurrentSession = captureSession();

        try {
            const options = await personOptionsService.getOptions(personId);
            if (isCurrentSession()) darkWebMonitoring.value = options.monitoring.dark_web.enabled;
        } catch (error) {
            // The close stands without the sentence, so a failed read is reported rather than shown.
            console.error('Failed to load dark web monitoring options:', error);
        }
    }

    return {
        // state
        footprint,
        seeds,
        depth,
        status,
        scanSession,
        sessionSecondsRemaining,
        sessionWarning,
        sessionExtending,
        sessionError,
        sessionNotice,
        truncated,
        noUsableSeeds,
        meta,
        sources,
        drawer,
        statusMessage,
        pollIntervalMs,
        access,
        darkWebMonitoring,
        awaitingStart,
        // getters
        allValues,
        inVaultCount,
        recordsFound,
        credentialsCount,
        sourceCount,
        hasFootprint,
        isScanning,
        isDeferred,
        isSourceUnavailable,
        deferralNotice,
        isError,
        isTimedOut,
        isExpired,
        isSettled,
        canDeepen,
        tier,
        showThreatHunter,
        credentialsInFull,
        hasSearchableSeed,
        isFamilyAddable,
        dispositions,
        decisions,
        levelFragments,
        undecidedCount,
        meCount,
        meList,
        familiarCount,
        excludedCount,
        vaultSeedCount,
        familyCount,
        familyList,
        actingIds,
        beginAct,
        endAct,
        vaultFoundList,
        levelSorted,
        maxDepth,
        explainerShown,
        // actions
        captureSession,
        onSessionEnd,
        checkSession,
        extendSession,
        endSession,
        loadFootprint,
        setSeeds,
        cancelPolling,
        refresh,
        beginDescent,
        negativeFields,
        setDisposition,
        setDispositionFor,
        setFamily,
        setFamilyFor,
        markExplainerShown,
        fragmentState,
        runDeeperScan,
        openDrawer,
        closeDrawer,
        loadMoreRecords,
        revealSensitive,
        loadMonitoring,
    };
});
